import { DateTime } from "luxon"
import { Badge, BodyContainer, Cell, Footer, Header, Row, Table } from "wed-components-beta"
import { Checkbox, IconButton, Tooltip } from "@mui/material"
import { formatNumberToMoney } from "../../../Utils"
import { TableFormat } from "./Billings"
import { Comment, ExpandLess, ExpandMore, FilterNone, Visibility } from "@mui/icons-material"
import { Fragment, useCallback, useEffect, useMemo, useState } from "react"

type Props = {
  data: TableFormat[]
  enableMultipleSelect: boolean
  handleOpenBillings: (ids: number[]) => void
  months: DateTime[]
  selectedInstallments: number[]
  selectedRows: number[]
  setSelectedInstallments: React.Dispatch<React.SetStateAction<number[]>>
  setSelectedRows: React.Dispatch<React.SetStateAction<number[]>>
}

function defineStatusColor(status: string) {
  switch (status) {
    case 'Admitted':
      return 'green'
    case 'Canceled':
      return 'red'
    case 'Pending':
      return 'yellow'
    case 'In Graduation':
      return 'orange'
    case 'Graduate':
      return 'blue'
    case 'Applicant':
      return 'darkPurple'
    default:
      return 'blue'
  }
}

export default function BillingsTable({ data, enableMultipleSelect, handleOpenBillings, months, selectedInstallments, selectedRows, setSelectedInstallments, setSelectedRows }: Props) {
  console.log(data)
  const columnWidth = [.4, .6, 3, .8, 2.5, 1.2, 1, .8]
  const installmentWidth = 1.5
  const [expandedRows, setExpandedRows] = useState<string[]>([])

  const onScrollLeft = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    document.getElementById('body-container-right')?.scrollTo({ top: e.currentTarget.scrollTop })
  }

  const onScrollRight = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    document.getElementById('body-container-left')?.scrollTo({ top: e.currentTarget.scrollTop })
  }

  // const onSelectMonth = useCallback((month: string, checked: boolean) => {
  //   const ids: number[] = []

  //   for (const customer of data) {
  //     for (const billing of customer.billings) {
  //       for (const cashFlow of billing.cashFlow) {
  //         if (cashFlow.month === month) ids.push(...cashFlow.ids)
  //       }
  //     }
  //   }

  //   setSelectedInstallments((prev) => checked ? prev.concat(ids) : prev.filter((id) => !ids.includes(id)))
  // }, [data, setSelectedInstallments])

  // const monthInstallmentIds = useMemo(() => {
  //   const map = new Map<string, number[]>()

  //   for (const customer of data) {
  //     for (const billing of customer.billings) {
  //       for (const cashFlow of billing.cashFlow) {
  //         if (map.has(cashFlow.month)) map.get(cashFlow.month)!.push(...cashFlow.ids)
  //         else map.set(cashFlow.month, cashFlow.ids)
  //       }
  //     }
  //   }

  //   return map
  // }, [data, months])

  // const isMonthChecked = useCallback((month: string) => {
  //   const ids = monthInstallmentIds.get(month)!

  //   return selectedInstallments.every((id) => ids.includes(id))
  // }, [monthInstallmentIds, selectedInstallments])

  const handleCheckboxCustomer = useCallback((ids: number[], customerName: string, checked: boolean) => {
    if (checked) {
      setSelectedRows((prev) => prev.concat(ids))
      setExpandedRows((prev) => prev.concat(customerName))
      return
    }

    setSelectedRows((prev) => prev.filter((id) => !ids.includes(id)))
  }, [setSelectedRows, setExpandedRows])

  return <Table>
    <BodyContainer
      id="body-container-left"
      flexShrink={0}
      onScroll={onScrollLeft}
    >
      <Header style={{ zIndex: 1 }}>
        {
          enableMultipleSelect ?
            <Cell width={columnWidth[0]}>
              <Checkbox
                size="small"
                disableRipple
                checked={selectedRows.length === data.length && data.length !== 0}
                onChange={(e) => setSelectedRows(() => e.target.checked ? data.flatMap((billing) => billing.billings.flatMap((_) => _.id)) : [])}
              />
            </Cell>
            :
            null
        }
        <Cell width={columnWidth[1]}>
          ID
        </Cell>
        <Cell width={columnWidth[2]}>
          Beneficiary/Payer
        </Cell>
        <Cell width={columnWidth[3]}>
          Grade
        </Cell>
        <Cell width={columnWidth[4]}>
          Bundle/Def. Bill.
        </Cell>
        <Cell width={columnWidth[5]}>
          Status
        </Cell>
      </Header>
      {
        !data.length ?
          <Row>
            <Cell flexGrow>No customers to show.</Cell>
          </Row>
          :
          data.map((customer, index) => <Fragment key={index}>
            <Row
              onDoubleClick={() => handleOpenBillings(customer.billings.map((_) => _.id))}
              style={customer.billings.length > 1 ? { backgroundColor: 'rgba(0, 0, 0, 0.5)' } : {}}
            >
              {
                enableMultipleSelect ?
                  <Cell width={columnWidth[0]}>
                    {
                      customer.billings.length === 1 ?
                        <Checkbox
                          size="small"
                          disableRipple
                          checked={selectedRows.includes(customer.billings[0].id)}
                          onChange={(e) => setSelectedRows((rows) => e.target.checked ? rows.concat(customer.billings[0].id) : rows.filter((id) => id !== customer.billings[0].id))}
                        />
                        :
                        <Checkbox
                          size="small"
                          disableRipple
                          indeterminate={customer.billings.map((_) => _.id).some((_) => selectedRows.includes(_)) && !customer.billings.map((_) => _.id).every((_) => selectedRows.includes(_))}
                          checked={customer.billings.map((_) => _.id).every((_) => selectedRows.includes(_))}
                          onChange={(e) => handleCheckboxCustomer(customer.billings.map((_) => _.id), customer.customerName, e.target.checked)}
                        />
                    }
                  </Cell>
                  :
                  null
              }
              <Cell width={columnWidth[1]}>
                {
                  customer.billings.length === 1 ?
                    customer.billings[0].id
                    :
                    expandedRows.includes(customer.customerName) ?
                      <Tooltip title="Hide billings">
                        <IconButton
                          sx={{ width: 30, height: 30 }}
                          onClick={() => setExpandedRows((rows) => rows.filter((name) => name !== customer.customerName))}
                        >
                          <ExpandLess />
                        </IconButton>
                      </Tooltip>
                      :
                      <Tooltip title="Show billings">
                        <IconButton
                          sx={{ width: 30, height: 30 }}
                          onClick={() => setExpandedRows((rows) => rows.concat(customer.customerName))}
                        >
                          <ExpandMore />
                        </IconButton>
                      </Tooltip>
                }
              </Cell>
              <Cell width={columnWidth[2]} style={{ flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center', gap: 10 }}>
                <Tooltip title='View student profile'>
                  <IconButton
                    id={`button-open-student-profile-${customer.id}`}
                    sx={{ width: 28, height: 28 }}
                    onClick={() => window.open(`/students/${customer.id}`, '_blank')}
                  >
                    <Visibility fontSize="small" />
                  </IconButton>
                </Tooltip>
                {customer.affiliationTag ? <Badge color='strongYellow'>{customer.affiliationTag}</Badge> : null}
                {customer.customerName}
              </Cell>
              <Cell width={columnWidth[3]}>
                {customer.gradeName}
              </Cell>
              <Cell width={columnWidth[4]}>
                {customer.bundleDefaultBilling}
              </Cell>
              <Cell width={columnWidth[5]}>
                <Badge color={defineStatusColor(customer.studentStatusTypeName)}>
                  {customer.studentStatusTypeName}
                </Badge>
              </Cell>
            </Row>
            {
              expandedRows.includes(customer.customerName) ?
                customer.billings.map((value, valueIndex) => <Row key={valueIndex} onDoubleClick={() => handleOpenBillings(customer.billings.map((_) => _.id))}>
                  {
                    enableMultipleSelect ?
                      <Cell width={columnWidth[0]}>
                        <Checkbox
                          size="small"
                          disableRipple
                          checked={selectedRows.includes(value.id)}
                          onChange={(e) => setSelectedRows((rows) => e.target.checked ? rows.concat(value.id) : rows.filter((id) => id !== value.id))}
                        />
                      </Cell>
                      :
                      null
                  }
                  <Cell width={columnWidth[1]}>
                    {value.id}
                  </Cell>
                  <Cell flexGrow>
                    {value.defaultBillingName}
                  </Cell>
                </Row>)
                :
                null
            }
          </Fragment>)
      }
      <Footer>
        <Cell flexGrow>
          {data.length} students
        </Cell>
      </Footer>
    </BodyContainer>
    {/* Installments Container */}
    <BodyContainer
      id="body-container-right"
      allowCellOverflow
      onScroll={onScrollRight}
    >
      <Header>
        <Cell width={columnWidth[6]}>
          Line Total
        </Cell>
        <Cell width={columnWidth[7]}>
          Installments
        </Cell>
        {
          months.map((_, index) => <Cell
            key={index}
            width={installmentWidth}
            style={{
              flexDirection: 'row',
              justifyContent: 'flex-start',
              alignItems: 'center'
            }}
          >
            {
              // enableMultipleSelect ?
              //   <Checkbox
              //     size="small"
              //     disableRipple
              //     indeterminate={selectedInstallments.length > 0}
              //     checked={isMonthChecked(_.toFormat('MMM yy'))}
              //     onChange={(e) => onSelectMonth(_.toFormat('MMM yy'), e.target.checked)}
              //   />
              //   :
              //   null
            }
            {_.toFormat('MMM yy')}
          </Cell>)
        }
      </Header>
      {
        data.map((customer, index) => <Fragment key={index}>
          <Row
            onDoubleClick={() => handleOpenBillings(customer.billings.map((_) => _.id))}
            style={customer.billings.length > 1 ? { backgroundColor: 'rgba(0, 0, 0, 0.5)' } : {}}
          >
            {/* Line Total */}
            <Cell width={columnWidth[6]}>
              {
                customer.billings
                  .map((_) => ({ currencySign: _.currencySign, totalDueValue: _.totalDueValue }))
                  .reduce((prev: { currencySign: string, totalDueValue: number }[], curr) => {
                    const index = prev.findIndex((_) => _.currencySign === curr.currencySign)

                    if (index !== -1) {
                      prev[index].totalDueValue += curr.totalDueValue
                    }
                    else {
                      prev.push(curr)
                    }

                    return prev
                  }, [])
                  .map((_, valueIndex) => <p key={valueIndex}>{_.currencySign} {formatNumberToMoney(_.totalDueValue)}</p>)
              }
            </Cell>
            {/* Number of installments */}
            <Cell width={columnWidth[7]}>
              {customer.billings.map((_) => _.numberOfInstallments).reduce((prev, curr) => prev += curr, 0)}
            </Cell>
            {/* Values */}
            {
              months.map((month, monthIndex) => {
                const values = customer.billings.flatMap((_) => _.cashFlow).filter((cashFlow) => cashFlow.month === month.toFormat('MMM yy'))

                if (!values.length) return <Cell key={monthIndex} width={installmentWidth}>-</Cell>

                return <Cell
                  key={monthIndex}
                  width={installmentWidth}
                >
                  {customer.billings[0].currencySign} {formatNumberToMoney(values.reduce((prev, curr) => prev += curr.dueValue, 0))}
                </Cell>
              })
            }
          </Row>
          {
            expandedRows.includes(customer.customerName) ?
              customer.billings.map((value, expandedRowsIndex) => <Row key={expandedRowsIndex} onDoubleClick={() => handleOpenBillings(customer.billings.map((_) => _.id))}>
                {/* Line Total */}
                <Cell width={columnWidth[6]}>
                  {value.currencySign} {formatNumberToMoney(value.totalDueValue)}
                </Cell>
                {/* Number of installments */}
                <Cell width={columnWidth[7]}>
                  {value.numberOfInstallments}
                </Cell>
                {/* Values */}
                {
                  months.map((month, monthIndex) => {
                    const installment = value.cashFlow.find((cashFlow) => cashFlow.month === month.toFormat('MMM yy'))

                    if (!installment) return <Cell key={monthIndex} width={installmentWidth}>-</Cell>

                    return <Cell
                      key={monthIndex}
                      width={installmentWidth}
                      style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                      {
                        // enableMultipleSelect ?
                        //   <Checkbox
                        //     size="small"
                        //     disableRipple
                        //     checked={installment.ids.every((_id) => selectedInstallments.includes(_id))}
                        //     onChange={(e) => setSelectedInstallments((prev) => e.target.checked ? prev.concat(installment.ids) : prev.filter((_id) => !installment.ids.includes(_id)))}
                        //   />
                        //   :
                        //   null
                      }
                      <p style={installment.isPaid ? { color: 'lightgreen' } : {}}>
                        {value.currencySign} {formatNumberToMoney(installment.dueValue)}
                      </p>
                      {
                        installment.numberOfInstallments > 1 ?
                          <Tooltip title={`${installment.numberOfInstallments} installments`}>
                            <FilterNone />
                          </Tooltip>
                          :
                          null
                      }
                      {
                        installment.comments.length ?
                          <Tooltip title={installment.comments.map((comment, commentIndex) => <p key={commentIndex}>{comment}</p>)}>
                            <Comment />
                          </Tooltip>
                          :
                          null
                      }
                    </Cell>
                  })
                }
              </Row>)
              :
              null
          }
        </Fragment>)
      }
      <Footer>
        <Cell width={columnWidth[6]}>
          {
            data
              .map((customer) => ({ currencySign: customer.billings[0].currencySign, total: customer.billings.map((_) => _.totalDueValue).reduce((prev, curr) => prev += curr, 0) }))
              .reduce((prev: { currencySign: string, total: number }[], curr) => {
                const index = prev.findIndex((_) => _.currencySign === curr.currencySign)

                if (index !== -1) {
                  prev[index].total += curr.total
                }
                else {
                  prev.push(curr)
                }

                return prev
              }, [])
              .map((_, index) => <Cell key={index}>{_.currencySign} {formatNumberToMoney(_.total)}</Cell>)
          }
        </Cell>
        <Cell width={columnWidth[7]}>
          {
            data.flatMap((customer) => customer.billings.flatMap((_) => _.numberOfInstallments)).reduce((prev, curr) => prev += curr, 0)
          }
        </Cell>
        {
          months
            .map((month, index) => <Cell key={index} width={installmentWidth}>
              {
                data
                  .map((customer) => ({
                    currencySign: customer.billings[0].currencySign,
                    total: customer.billings.flatMap((value) => value.cashFlow).filter((cashFlow) => cashFlow.month === month.toFormat('MMM yy')).reduce((prev, curr) => prev += curr.dueValue, 0)
                  }))
                  .reduce((prev: { currencySign: string, total: number }[], curr) => {
                    const index = prev.findIndex((_) => _.currencySign === curr.currencySign)

                    if (index !== -1) {
                      prev[index].total += curr.total
                    }
                    else {
                      prev.push(curr)
                    }

                    return prev
                  }, [])
                  .map((_, valueIndex) => <p key={valueIndex}>{_.currencySign} {formatNumberToMoney(_.total)}</p>)
              }
            </Cell>)
        }
      </Footer>
    </BodyContainer>
  </Table >
}